import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaClock, FaReceipt, FaUtensils } from "react-icons/fa";
import { SESSION_UPDATED_EVENT } from "../utils/accountLinks";
import {
  clearImpersonationStorage,
  isImpersonating,
  isMasterSession,
  setStoredSession,
} from "../utils/impersonation";
import { resetLogRocketSession } from "../utils/logRocket";

/**
 * BillSarthi Web Dashboard — aligned with mobile dashboard
 * - Uses /getorderlist with today's date (fromdate/todate)
 * - Shows same core fields: ordercode, totalamount, created_at, tableid, added_by_name
 * - Offline sync using localStorage("createorderitem")
 */

const BRAND = "#015b9a";
const ACCENT = "#14f1a2";
const Dashboard = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [offlineCount, setOfflineCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [deletingOrderId, setDeletingOrderId] = useState(null);
  const [tableMetaMap, setTableMetaMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    const pad = (v) => String(v).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  });

  /* ------------------------------ helpers ------------------------------ */
  const formatINR = (num) => {
    const n = Number(num || 0);
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const parseAnyDate = (d) => {
    if (!d) return new Date(0);
    const isoish = typeof d === "string" ? d.replace(" ", "T") : d;
    const dt = new Date(isoish);
    return isNaN(dt.getTime()) ? new Date(0) : dt;
  };

  const pad2 = (v) => String(v).padStart(2, "0");

  const formatDate = (date) => {
    const d = parseAnyDate(date);
    return `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;
  };

  /* ---------- table helpers (parity with mobile dashboard) ---------- */
  const isTakeawayName = (name) =>
    typeof name === "string" && name.toLowerCase().startsWith("takeaway");

  const extractTakeawayIndex = (name) => {
    if (!name) return null;
    const lower = String(name).toLowerCase();
    if (lower === "takeaway") return 1;
    const match = lower.match(/takeaway[\\s-]*(\\d+)/);
    if (match) {
      const num = parseInt(match[1], 10);
      return Number.isNaN(num) ? null : num;
    }
    return null;
  };

  const formatTakeawayLabel = (name) => {
    const raw =
      name === null || name === undefined ? "" : String(name).trim();

    // Dashboard API sometimes sends table id/name as 0 or -1 for takeaways
    if (!raw || raw === "0" || raw === "-1") return "TakeAway";

    const idx = extractTakeawayIndex(raw);
    return idx ? `TakeAway ${idx}` : raw;
  };

  const getTableKey = (table) => {
    if (!table) return "";
    const candidate =
      table?.table_key ??
      table?.tableKey ??
      table?.key ??
      table?.name ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      "";
    return candidate === null || candidate === undefined ? "" : String(candidate);
  };

  const getTableDisplayName = (table) => {
    if (!table) return "";
    const candidate =
      table?.display_name ??
      table?.displayName ??
      table?.table_key ??
      table?.tableKey ??
      table?.name ??
      table?.table ??
      table?.tableid ??
      table?.tableId ??
      table?.id ??
      "";
    return candidate === null || candidate === undefined ? "" : String(candidate);
  };

  const getTableType = (table) => {
    const raw =
      table?.table_type ?? table?.tableType ?? table?.tabletype ?? table?.type;
    return String(raw || "").toLowerCase() === "room" ? "room" : "table";
  };

  const buildTableMetaMap = (list = []) => {
    const map = {};
    if (!Array.isArray(list)) return map;
    list.forEach((entry) => {
      const key = getTableKey(entry);
      const numericName = entry?.name;
      const displayName = getTableDisplayName(entry);
      const type = getTableType(entry);
      const resolvedDisplay =
        displayName ||
        (key ? String(key) : numericName !== undefined ? String(numericName) : "");
      const meta = {
        displayName: resolvedDisplay,
        type,
      };
      const addKey = (k) => {
        if (k === null || k === undefined || k === "") return;
        map[String(k)] = meta;
      };
      addKey(key);
      addKey(numericName);
      addKey(entry?.id);
      addKey(entry?.tableid);
      addKey(entry?.tableId);
      addKey(entry?.table_id);
    });
    return map;
  };

  const resolveTableLabel = (order) => {
    const rawKey =
      order?.tableid ??
      order?.table ??
      order?.table_no ??
      order?.tableid ??
      order?.tablename ??
      order?.table_name ??
      "";
    const key = rawKey === null || rawKey === undefined ? "" : String(rawKey);
    const isTakeaway =
      key === "" ||
      key === "0" ||
      key === "-1" ||
      Number(key) === 0 ||
      Number(key) === -1 ||
      isTakeawayName(key);

    if (isTakeaway) {
      return { label: formatTakeawayLabel(key || "TakeAway-1"), type: "takeaway" };
    }

    const meta =
      tableMetaMap[key] ||
      tableMetaMap[String(order?.tableid)] ||
      tableMetaMap[String(order?.table)] ||
      {};
    const typeRaw =
      (
        order?.table_type ||
        order?.tableType ||
        order?.tabletype ||
        ""
      )
        .toString()
        .toLowerCase();

    // Heuristics to match mobile: prefer meta; fall back to order fields containing "room"
    const displayCandidate =
      meta?.displayName ||
      order?.table_label ||
      order?.tablename ||
      order?.table_name ||
      order?.table ||
      key ||
      "-";
    const displayLower =
      typeof displayCandidate === "string"
        ? displayCandidate.toLowerCase()
        : "";

    const looksRoom =
      typeRaw === "room" ||
      (displayLower && /ro+om/.test(displayLower));

    const type = meta?.type || (looksRoom ? "room" : "table");

    const normalizeLabel = (val = "") => {
      if (typeof val !== "string") return String(val || "");
      if (!val.trim()) return "-";
      const first = val.trim();
      return first.charAt(0).toUpperCase() + first.slice(1);
    };

    let label;
    if (type === "room") {
      if (displayLower.startsWith("room")) {
        label = normalizeLabel(displayCandidate);
      } else {
        label = `Room ${normalizeLabel(displayCandidate)}`;
      }
    } else {
      label = `Table ${normalizeLabel(displayCandidate)}`;
    }

    const displayName =
      typeof displayCandidate === "string" && displayCandidate.trim()
        ? displayCandidate
        : key || "-";

    return { label, type, displayName };
  };

  const getRelativeTime = (date) => {
    const now = Date.now();
    const t = parseAnyDate(date).getTime();
    const diffMs = Math.max(0, now - t);
    const mins = Math.floor(diffMs / (1000 * 60));
    const hrs = Math.floor(mins / 60);
    const days = Math.floor(hrs / 24);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  };

  const fetchOrders = useCallback(
    async (restaurantid, opts = {}) => {
      try {
        setLoading(true);

        const body = {
          restaurantid,
          fromdate: selectedDate,
          todate: selectedDate,
        };

        const response = await fetch(
          "https://qr.billsarthi.in/public/api/getorderlist",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result?.message || "Failed to load orders");
        }

        const list = Array.isArray(result?.list) ? result.list : [];
        const sorted = [...list].sort(
          (a, b) =>
            parseAnyDate(b?.created_at).getTime() -
            parseAnyDate(a?.created_at).getTime()
        );

        setOrders(sorted);
        setStatus("");
      } catch (err) {
        console.error("Order fetch failed", err);
        toast(err?.message || "Failed to load orders", "error");
        setStatus("Offline mode — showing last loaded results (if any).");
      } finally {
        setLoading(false);
      }
    },
    [selectedDate]
  );

  const loadTableMeta = useCallback(
    async (restaurantid) => {
      // try cache first
      try {
        const cachedRaw = localStorage.getItem("tablelistx");
        if (cachedRaw) {
          const cachedList = JSON.parse(cachedRaw);
          if (Array.isArray(cachedList)) {
            setTableMetaMap(buildTableMetaMap(cachedList));
          }
        }
      } catch {}

      if (!restaurantid) return;
      try {
        const response = await fetch(
          "https://qr.billsarthi.in/public/api/list/table",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid }),
          }
        );
        const result = await response.json();
        if (response.ok && Array.isArray(result?.list)) {
          const map = buildTableMetaMap(result.list);
          setTableMetaMap(map);
          localStorage.setItem("tablelistx", JSON.stringify(result.list));
        }
      } catch (e) {
        console.log("table list load failed on dashboard:", e?.message || e);
      }
    },
    []
  );

  const checkOfflineOrders = useCallback(() => {
    const stored = localStorage.getItem("createorderitem");
    if (stored) {
      try {
        const list = JSON.parse(stored);
        setOfflineCount(Array.isArray(list) ? list.length : 0);
      } catch {
        setOfflineCount(0);
      }
    } else {
      setOfflineCount(0);
    }
  }, []);

  const readStoredDashboardUser = useCallback(() => {
    const stored = localStorage.getItem("userData");
    if (!stored) return null;

    try {
      const parsed = JSON.parse(stored);
      return parsed?.data || parsed || null;
    } catch {
      return null;
    }
  }, []);

  const syncDashboardUserFromStorage = useCallback(() => {
    const nextUser = readStoredDashboardUser();
    setUserData(nextUser);
    checkOfflineOrders();
    return nextUser;
  }, [checkOfflineOrders, readStoredDashboardUser]);

  const forceLogout = useCallback(() => {
    clearImpersonationStorage();
    localStorage.removeItem("webLoginCredentials");
    resetLogRocketSession();
    localStorage.removeItem("userData");
    setUserData(null);
    navigate("/login", { replace: true });
  }, [navigate]);

  /* ------------------------------ effects ------------------------------ */
  useEffect(() => {
    const nextUser = syncDashboardUserFromStorage();
    if (!nextUser) {
      navigate("/login");
      return;
    }
  }, [navigate, syncDashboardUserFromStorage]);

  useEffect(() => {
    const handleSessionUpdated = () => {
      const nextUser = syncDashboardUserFromStorage();
      if (!nextUser) {
        navigate("/login", { replace: true });
      }
    };

    window.addEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
    window.addEventListener("storage", handleSessionUpdated);
    return () => {
      window.removeEventListener(SESSION_UPDATED_EVENT, handleSessionUpdated);
      window.removeEventListener("storage", handleSessionUpdated);
    };
  }, [navigate, syncDashboardUserFromStorage]);

  useEffect(() => {
    if (!userData?.id) return;
    if (isImpersonating() || isMasterSession(userData)) return;

    let session = {};
    try {
      session = JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      session = {};
    }

    const storedUser = session?.data || session || {};
    const mobile_number = String(
      storedUser?.mobile_number || storedUser?.phone || ""
    ).trim();
    const password = String(storedUser?.password || "");

    if (!mobile_number || !password) return;

    let cancelled = false;

    const refreshWebAccess = async () => {
      try {
        const response = await fetch(
          "https://qr.billsarthi.in/public/api/login/restro",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mobile_number,
              password,
            }),
          }
        );

        const result = await response.json().catch(() => ({}));
        if (!response.ok) return;

        const rawIsWebAllowed =
          result?.data?.isWebAllowed ??
          result?.data?.isweballowed ??
          result?.isWebAllowed ??
          result?.isweballowed;

        if (
          rawIsWebAllowed === 0 ||
          rawIsWebAllowed === "0" ||
          rawIsWebAllowed === false
        ) {
          if (!cancelled) forceLogout();
          return;
        }

        if (!cancelled && result?.data) {
          setStoredSession(result);
          setUserData(result.data);
        }
      } catch (error) {
        console.log(
          "dashboard login/restro refresh skipped:",
          error?.message || error
        );
      }
    };

    refreshWebAccess();

    return () => {
      cancelled = true;
    };
  }, [forceLogout, userData?.id]);

  useEffect(() => {
    if (userData?.id) {
      fetchOrders(userData.id);
      loadTableMeta(userData.id);
    }
  }, [userData?.id, selectedDate, fetchOrders, loadTableMeta]);

  /* ------------------------------ actions ------------------------------ */
  const handleSync = async () => {
    try {
      const stored = localStorage.getItem("createorderitem");
      if (!stored) {
        toast("No pending offline orders", "info");
        return;
      }

      const queue = JSON.parse(stored);
      if (!Array.isArray(queue) || queue.length === 0) {
        toast("No pending offline orders", "info");
        return;
      }

      // match mobile: bulk endpoint
      const response = await fetch(
        "https://qr.billsarthi.in/public/api/create/order/bulk",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orders: queue }),
        }
      );
      const json = await response.json();

      if (response.ok && json?.success) {
        toast(`Synced ${queue.length} orders`, "success");
        localStorage.removeItem("createorderitem");
        setOfflineCount(0);
        if (userData?.id) fetchOrders(userData.id, { refresh: true });
      } else {
        toast(json?.message || "No orders synced. Check connection.", "error");
      }
    } catch (err) {
      toast(err?.message || "Sync failed", "error");
    }
  };

  const handleRefresh = () => {
    const latestUser = readStoredDashboardUser();
    const latestRestaurantId = Number(latestUser?.id || 0);
    const currentRestaurantId = Number(userData?.id || 0);

    if (!latestRestaurantId && !currentRestaurantId) return;

    if (latestUser && latestRestaurantId !== currentRestaurantId) {
      setUserData(latestUser);
      checkOfflineOrders();
      return;
    }

    const restaurantId = latestRestaurantId || currentRestaurantId;
    fetchOrders(restaurantId, { refresh: true });
    loadTableMeta(restaurantId);
  };

  const deleteOrderFromDashboard = async (order) => {
    if (!order?.id || !userData?.id) return;
    setDeletingOrderId(order?.id ?? true);
    try {
      const response = await fetch(
        "https://qr.billsarthi.in/public/api/dashboard/delete-order",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            orderid: order.id,
            restaurantid: userData?.id,
            deleted_by: userData?.id,
          }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast("Order deleted", "success");
        fetchOrders(userData.id, { refresh: true });
      } else {
        toast(result?.message || "Failed to delete order", "error");
      }
    } catch (error) {
      toast("Network error", "error");
    } finally {
      setDeletingOrderId(null);
    }
  };

  useEffect(() => {
    const onSync = () => handleSync();
    const onRefresh = () => handleRefresh();
    window.addEventListener("dashboard:sync", onSync);
    window.addEventListener("dashboard:refresh", onRefresh);
    return () => {
      window.removeEventListener("dashboard:sync", onSync);
      window.removeEventListener("dashboard:refresh", onRefresh);
    };
  }, [handleSync, handleRefresh]);

  /* ------------------------------ derived ------------------------------ */
  const totals = useMemo(() => {
    const list = Array.isArray(orders) ? orders : [];
    const count = list.length;
    const amount = list.reduce(
      (sum, o) => sum + Number(o?.totalamount || 0),
      0
    );
    return { count, amount };
  }, [orders]);

  /* --------------------------------- UI --------------------------------- */
  return (
    <div style={sx.page}>
      {/* Welcome strip */}
      {/* <section style={sx.welcomeStrip}>
        <div style={sx.welcomeLeft}>
          <div style={sx.hello}>Welcome back,</div>
          <div style={sx.restName}>
            {userData?.restaurant_name || "Restaurant"}
          </div>
        </div>
        {offlineCount > 0 ? (
          <div style={sx.syncPill} onClick={handleSync} role="button">
            <span>☁️</span>
            <span style={{ fontWeight: 800 }}> {offlineCount} pending</span>
            <span style={{ opacity: 0.85 }}> • click to sync</span>
          </div>
        ) : null}
      </section> */}

      {/* Stats */}
      <section style={{ ...sx.statsRow, ...sx.anim.fadeUp(0.04) }}>
        <div style={{ ...sx.statCard, ...sx.anim.riseIn(0.08) }}>
          <div style={sx.statTopRow}>
            <div style={sx.statTitle}>Total Orders</div>
            <div style={sx.todayTag}>Today</div>
          </div>
          <div style={sx.statValue}>{totals.count}</div>
        </div>
        <div style={{ ...sx.statCard, ...sx.anim.riseIn(0.16) }}>
          <div style={sx.statTopRow}>
            <div style={sx.statTitle}>Total Amount</div>
            <div style={sx.todayTag}>Today</div>
          </div>
          <div style={sx.statValue}>₹ {formatINR(totals.amount)}</div>
        </div>
      </section>

      {/* Orders */}
      <section>
        <div style={{ ...sx.sectionHeader, ...sx.anim.fadeUp(0.1) }}>
          <h3 style={sx.sectionTitle}>Today’s Orders</h3>
          <div style={sx.datePickerWrap}>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={sx.dateInput}
              aria-label="Select date"
            />
          </div>
        </div>

        {status ? <div style={sx.statusBanner}>{status}</div> : null}

        {loading ? (
          <div style={{ ...sx.loaderWrap, ...sx.anim.fadeUp(0.12) }}>
            <div style={sx.spinner} />
            <div style={{ marginTop: 8, opacity: 0.9 }}>Loading orders…</div>
          </div>
        ) : !orders || orders.length === 0 ? (
          <div style={{ ...sx.emptyWrap, ...sx.anim.fadeUp(0.12) }}>
            <div style={sx.emptyBadge}>No orders yet</div>
            <p style={{ opacity: 0.8, marginTop: 6 }}>
              New orders will show up here.
            </p>
          </div>
        ) : (
          <div style={sx.grid}>
            {orders.map((order, index) => {
              const tableInfo = resolveTableLabel(order);
              const isTakeaway = tableInfo.type === "takeaway";
              // Debug: log how table labels are being resolved (remove after verification)
              console.log("Dashboard table label", {
                orderId: order?.id || order?.ordercode,
                tableid: order?.tableid,
                table: order?.table,
                table_type: order?.table_type || order?.tableType || order?.tabletype,
                tableLabel: tableInfo.label,
                tableType: tableInfo.type,
                tableMetaKeys: Object.keys(tableMetaMap || {}).length,
              });
              return (
                <article
                  key={order.id || order.ordercode}
                  style={{ ...sx.card, ...sx.anim.riseIn(0.12 + index * 0.04) }}
                >
                  {/* Header */}
                  <div style={sx.cardHead}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <div style={sx.avatar}>
                        <FaReceipt />
                      </div>
                      <div
                        style={{
                          fontWeight: 800,
                          color: "#0f172a",
                          fontSize: 15,
                        }}
                      >
                        #{order?.ordercode || order?.id}
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div style={sx.timeChip}>
                        <FaClock size={11} />
                        {getRelativeTime(order?.created_at)}
                      </div>
                      <div style={sx.metaChip}>
                        <FaUtensils size={11} />
                        {tableInfo.label}
                      </div>
                    </div>
                  </div>

                  {/* Created by */}
                  <div style={sx.createdBy}>
                    {order?.added_by_name
                      ? `Created by ${order.added_by_name}`
                      : "Created by Owner"}
                  </div>

                  <div style={sx.hr} />

                  {/* Summary */}
                  <div style={sx.rowBetween}>
                    <div>
                      <div style={sx.metaLabel}>Total Amount</div>
                      <div style={sx.totalValue}>
                        ₹ {formatINR(order?.totalamount)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={sx.metaLabel}>Placed on</div>
                      <div style={sx.metaValue}>
                        {formatDate(order?.created_at)}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <button
                    className="dashboard-view-btn"
                    style={sx.viewBtn}
                    onClick={() =>
                      navigate(`/print/${order?.id || order?.orderid || ""}`, {
                        state: { order },
                      })
                    }
                  >
                    View Receipt
                  </button>
                  <div style={sx.actionRow}>
                    <button
                      style={{
                        ...sx.viewBtn,
                        marginTop: 8,
                        background: "#fff",
                        color: BRAND,
                        flex: 1,
                      }}
                      onClick={() => {
                        const ok = window.confirm(
                          "Are you sure you want to edit this order?"
                        );
                        if (!ok) return;
                        navigate(
                          `/order/${encodeURIComponent(
                            order?.tableid || order?.table || "TakeAway-1"
                          )}`,
                          {
                            state: {
                              orderForEdit: order,
                              tableType: tableInfo.type === "takeaway" ? "table" : tableInfo.type,
                              tableLabel: tableInfo.displayName || tableInfo.label,
                            },
                          }
                        );
                      }}
                    >
                      Edit Order
                    </button>
                    {userData?.role === "owner" &&
                    userData?.allowdelete == 1 ? (
                      <button
                        style={{
                          ...sx.viewBtn,
                          marginTop: 8,
                          background: "#fee2e2",
                          color: "#b91c1c",
                          borderColor: "rgba(185,28,28,.35)",
                          flex: 1,
                        }}
                        onClick={() => {
                          const ok = window.confirm(
                            "Are you sure you want to delete this order?"
                          );
                          if (!ok) return;
                          deleteOrderFromDashboard(order);
                        }}
                        disabled={deletingOrderId === order?.id}
                      >
                        {deletingOrderId === order?.id
                          ? "Deleting…"
                          : "Delete"}
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Toast root */}
      <div id="toast-root" style={sx.toastRoot} />
    </div>
  );
};

/* ------------------------------- styles ------------------------------- */
const sx = {
  page: {
    minHeight: "100vh",
    // background:
    //   "radial-gradient(920px 360px at -8% -10%, rgba(1,91,154,0.12), transparent 62%), radial-gradient(860px 320px at 108% -6%, rgba(34,197,94,0.08), transparent 60%), linear-gradient(180deg,#eef4fb 0%, #f4f8fe 45%, #f8fbff 100%)",
    color: "#0b1f2a",
    fontFamily:
      '"Sora","Plus Jakarta Sans","Segoe UI",Roboto,Arial,sans-serif',
  },

  welcomeStrip: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "18px 16px 14px",
    padding: "14px 18px",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 10px 24px rgba(15,23,42,.06)",
  },
  hello: { fontSize: 13, color: "#334155" },
  restName: { fontSize: 18, fontWeight: 900, color: BRAND, marginTop: 2 },
  welcomeLeft: { display: "flex", flexDirection: "column", gap: 2 },
  syncPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    background: "#0ea5e9",
    color: "#fff",
    padding: "8px 12px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.6)",
    boxShadow: "0 10px 24px rgba(14,165,233,.3)",
  },

  statsRow: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "18px 16px 22px",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },
  statCard: {
    background: "rgba(255,255,255,0.96)",
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 18,
    padding: 16,
    boxShadow:
      "0 10px 24px rgba(15,23,42,.07), inset 0 1px 0 rgba(255,255,255,.72)",
    backdropFilter: "blur(8px)",
  },
  statTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  statTitle: { fontSize: 13, fontWeight: 800, color: "#0f172a" },
  todayTag: {
    background: "linear-gradient(90deg,#e0f2fe,#ecfeff)",
    color: "#0f172a",
    padding: "3px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 900,
    color: "#0f172a",
    marginTop: 6,
  },

  sectionHeader: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "6px 16px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionTitle: {
    margin: 0,
    color: "#0f172a",
    fontSize: 18,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  datePickerWrap: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dateInput: {
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 12,
    padding: "7px 12px",
    fontWeight: 700,
    color: "#0f172a",
    background: "rgba(255,255,255,.92)",
    boxShadow: "0 8px 18px rgba(15,23,42,.06)",
  },

  grid: {
    width: "calc(100% - 32px)",
    maxWidth: "100%",
    margin: "0 16px 28px",
    display: "grid",
    gap: 14,
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  },
  card: {
    background: "rgba(255,255,255,0.97)",
    border: "1px solid rgba(15,23,42,.08)",
    borderRadius: 18,
    padding: 16,
    boxShadow:
      "0 14px 30px rgba(15,23,42,.08), inset 0 1px 0 rgba(255,255,255,.72)",
    backdropFilter: "blur(6px)",
  },
  cardHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    background: BRAND,
    color: "#fff",
  },
  timeChip: {
    background: "linear-gradient(90deg,#e5f6ff,#ecfeff)",
    border: "1px solid rgba(0,0,0,.06)",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  metaChip: {
    background: "linear-gradient(90deg,#eef2ff,#f5f3ff)",
    color: BRAND,
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 12,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
  },
  createdBy: { color: "#64748b", fontSize: 12, fontWeight: 600, marginTop: 8 },
  hr: { height: 1, background: "rgba(17,24,39,.08)", margin: "10px 0" },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
  },
  metaLabel: { color: "#6b7280", fontSize: 12, fontWeight: 800 },
  totalValue: { color: "#0f172a", fontSize: 18, fontWeight: 900, marginTop: 2 },
  metaValue: { color: "#0f172a", fontSize: 13, fontWeight: 800, marginTop: 2 },
  viewBtn: {
    marginTop: 12,
    width: "100%",
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid rgba(1,91,154,.2)",
    background: "linear-gradient(135deg,#015b9a,#00a0ff)",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  actionRow: {
    display: "flex",
    gap: 10,
    alignItems: "stretch",
  },

  loaderWrap: {
    width: "calc(100% - 32px)",
    margin: "20px 16px",
    display: "grid",
    placeItems: "center",
    color: "#0f172a",
  },
  spinner: {
    width: 28,
    height: 28,
    border: "3px solid #cfe9ff",
    borderTopColor: BRAND,
    borderRadius: "50%",
    animation: "spin .9s linear infinite",
  },

  emptyWrap: {
    width: "calc(100% - 32px)",
    margin: "20px 16px",
    textAlign: "center",
  },
  statusBanner: {
    width: "calc(100% - 32px)",
    margin: "0 16px 12px",
    padding: "10px 14px",
    background: "#e0f2fe",
    border: "1px solid #bfdbfe",
    borderRadius: 12,
    color: "#0f172a",
    fontWeight: 700,
  },
  emptyBadge: {
    display: "inline-block",
    padding: "8px 12px",
    background: "#fff",
    border: "1px solid rgba(17,24,39,.06)",
    borderRadius: 999,
    fontWeight: 800,
  },
  anim: {
    fadeUp: (delay = 0) => ({
      animation: `fadeUp .7s cubic-bezier(.2,.7,.2,1) ${delay}s both`,
      willChange: "transform, opacity",
    }),
    riseIn: (delay = 0) => ({
      animation: `riseIn .8s cubic-bezier(.16,.7,.2,1) ${delay}s both`,
      willChange: "transform, opacity",
    }),
  },

  toastRoot: {
    position: "fixed",
    right: 16,
    bottom: 16,
    display: "grid",
    gap: 8,
    zIndex: 100,
  },
};

/* ----------------------------- lightweight toast ----------------------------- */
function toast(msg, type = "info") {
  const root = document.getElementById("toast-root");
  if (!root) {
    // fallback
    alert(msg);
    return;
  }
  const el = document.createElement("div");
  el.style.padding = "10px 14px";
  el.style.borderRadius = "10px";
  el.style.backdropFilter = "blur(8px)";
  el.style.border = "1px solid rgba(0,0,0,.08)";
  el.style.color = "#0f172a";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 10px 28px rgba(0,0,0,.12)";
  el.style.background =
    type === "success"
      ? "linear-gradient(90deg,#a8ff78,#78ffd6)"
      : type === "error"
      ? "linear-gradient(90deg,#ffcccb,#ffd1d1)"
      : "linear-gradient(90deg,#e0f3ff,#bff3ff)";
  el.textContent = msg;
  root.appendChild(el);
  setTimeout(() => {
    el.style.transition = "all .4s ease";
    el.style.opacity = "0";
    el.style.transform = "translateY(10px)";
    setTimeout(() => el.remove(), 420);
  }, 2200);
}

/* keyframes (once) */
if (
  typeof document !== "undefined" &&
  !document.getElementById("dashboard-spin-style")
) {
  const styleTag = document.createElement("style");
  styleTag.id = "dashboard-spin-style";
  styleTag.innerHTML = `
    @keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
    @keyframes fadeUp{
      0%{opacity:0;transform:translateY(22px)}
      100%{opacity:1;transform:translateY(0)}
    }
    @keyframes riseIn{
      0%{opacity:0;transform:translateY(26px) scale(.95)}
      100%{opacity:1;transform:translateY(0) scale(1)}
    }
    .dashboard-view-btn{
      background-size: 200% 100%;
      background-position: 0% 50%;
      transition: background-position .45s ease, box-shadow .35s ease, transform .2s ease;
    }
    .dashboard-view-btn:hover{
      background-position: 100% 50%;
      box-shadow: 0 14px 30px rgba(1,91,154,.28);
      transform: translateY(-1px);
    }
  `;
  document.head.appendChild(styleTag);
}

export default Dashboard;
